import { Controller } from "@hotwired/stimulus"

// Keeps a GET form's "preserved facet" hidden fields in step with the live URL.
//
// Why this exists (CONEK-1587): an HTML GET form discards its action's query
// string and sends only its own fields, so a search box that must not drop the
// page's active filters has to carry them as hidden inputs. Ui::TableTopbarComponent
// renders those from `preserved_params` — a server-rendered snapshot.
//
// The topbar deliberately sits OUTSIDE the Turbo Frame it targets: inside it,
// every response would replace the input mid-typing and steal focus. The cost is
// that a chip or an inline filter INSIDE the frame changes the facets with a
// frame-only swap, leaving that snapshot stale — so the next search re-applied
// the very facet the user had just cleared.
//
// The URL is the one thing both sides agree on, and `ui--url-sync` already keeps
// it current on frame navigation. This controller rebuilds the declared fields
// from it after a frame load.
//
// Deliberately NOT on connect: at first paint the server's snapshot is the fresh
// truth, and it may legitimately hold a facet the URL does not — a default, or
// something derived from the session. Only a frame navigation can invalidate it,
// so only a frame navigation triggers a rebuild.
//
// Usage (the topbar wires this itself):
//   <form data-controller="ui--preserved-params"
//         data-ui--preserved-params-names-value='["status","tags[]"]'>
//     <div data-ui--preserved-params-target="container">…hidden fields…</div>
//
// `names` are the rendered field NAMES, not bare keys, so array facets
// (`tags[]`) round-trip through URLSearchParams unchanged.
//
// Without JS, or without ui--url-sync, the server-rendered snapshot still ships
// — the same behaviour as before this controller existed.
export default class extends Controller {
  static targets = ["container"]
  static values = { names: { type: Array, default: [] } }

  connect() {
    this._onFrameLoad = () => this.sync()
    // Frame loads are listened for on the document: the frame that changed the
    // facets is a sibling of this form, never a descendant. ui--url-sync runs on
    // the frame itself, so it has already pushed the new URL by the time this
    // bubbles up here.
    document.addEventListener("turbo:frame-load", this._onFrameLoad)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
  }

  sync() {
    if (!this.hasContainerTarget || this.namesValue.length === 0) return

    const params = new URLSearchParams(window.location.search)
    const container = this.containerTarget

    for (const name of this.namesValue) {
      container
        .querySelectorAll(`input[type="hidden"][name="${CSS.escape(name)}"]`)
        .forEach((el) => el.remove())

      for (const value of params.getAll(name)) {
        const field = document.createElement("input")
        field.type = "hidden"
        field.name = name
        field.value = value
        container.appendChild(field)
      }
    }
  }
}
